import React, { useContext, useEffect, useState, useCallback } from 'react'
import { Sidebar } from './sidebar'
import { useSidebarStore } from '@/stores/sidebarStore'
import { Topbar } from './topbar'
import { Projects } from './projects'
import { Events } from './events'
import { Team } from './team'
import Products from './products'
import { useNavigate, useParams, useSearchParams, useLocation } from 'react-router-dom'
import toast from 'react-hot-toast'
import { useCustomFetch } from '../../hooks/useCustomFetch'
import { AuthContext } from '../../context/AuthContext'
import { DashboardContext } from '../../context/DashboardContext'
import { Meetings } from './meetings'
import { Recruiting } from './recruiting'
import { Integrations } from './integrations'
import { WelcomeDashboard } from './WelcomeDashboard'
import { OnboardingWelcomePage } from './OnboardingWelcomePage'
import clsx from 'clsx'
import { RecentMeetings } from './RecentMeetings'
import { DynamicItems } from './dynamic-items/DynamicItems'
import { Chats } from './chats'
import Roadmap from './roadmap'
import { HomeNew } from './home-new'
import { Settings } from './team/settings'
import Competitors from './competitors'
import { MonitorSmartphone } from 'lucide-react'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { useIsMobile } from '@/hooks/useIsMobile'
import { useHandleSignOut } from '@/hooks/useHandleSignOut'

export function Dashboard() {
  const isMobile = useIsMobile()
  const handleSignOut = useHandleSignOut()

  const [isLoading, setIsLoading] = useState(true)
  const [searchParams] = useSearchParams()
  const { selectedMenu } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const { user } = useContext(AuthContext)
  const {
    refreshActiveWorkspace,
    activeWorkspace,
    setActiveWorkspace,
    upcomingMeetings,
    eventsLoading,
    projectsLoading,
  } = useContext(DashboardContext)
  const { filteredProjects, allProjects, products, meetings } = useContext(DashboardContext)
  const { isCollapsed } = useSidebarStore()
  const setSelectedMenu = (newSelectedMenu) => navigate(`/dashboard/${newSelectedMenu}`)
  const [showLoader, setShowLoader] = useState(true)
  const limit = 10
  const customFetch = useCustomFetch()

  // State for tracking pending invitations to determine team completion
  const [pendingInvitations, setPendingInvitations] = useState([])
  const [invitationsLoading, setInvitationsLoading] = useState(true)
  // Existing workspace members (accepted)
  const [workspaceMembers, setWorkspaceMembers] = useState([])
  const [membersLoading, setMembersLoading] = useState(true)
  // Mobile warning dialog state
  const [showMobileWarning, setShowMobileWarning] = useState(true)

  // Function to fetch pending invitations
  const fetchInvitations = useCallback(async () => {
    if (!user?.active_workspace) return

    try {
      const response = await customFetch(`/getInvitesByWorkspace/${user.active_workspace}`, 'GET')
      setPendingInvitations(response.users || [])
    } catch (error) {
      console.error('Error fetching invitations:', error)
      setPendingInvitations([])
    } finally {
      setInvitationsLoading(false)
    }
  }, [user?.active_workspace, customFetch])

  // Function to fetch accepted workspace members
  const fetchMembers = useCallback(async () => {
    if (!user?.active_workspace) return
    try {
      const response = await customFetch(`/getUsersByWorkspace/${user.active_workspace}`, 'GET')
      setWorkspaceMembers(response.users || [])
    } catch (error) {
      console.error('Error fetching workspace users:', error)
      setWorkspaceMembers([])
    } finally {
      setMembersLoading(false)
    }
  }, [user?.active_workspace, customFetch])

  // Helper function to check if a specific onboarding step is completed
  const checkStepCompletion = useCallback(
    (stepId) => {
      // First check if the step is marked as completed in workspace data
      const completedSteps = activeWorkspace?.onboarding_completed_steps || []
      if (completedSteps.includes(stepId)) {
        return true
      }

      // Fallback to checking actual data (for backward compatibility)
      switch (stepId) {
        case 'product':
          return products && products.length > 0
        case 'recordings':
          return meetings && meetings.length > 0
        case 'integrations':
          // Check for integrations other than calendar (which is set up automatically)
          return (
            user?.integrations &&
            Object.entries(user.integrations).some(
              ([key, value]) =>
                Boolean(value) &&
                !key.toLowerCase().includes('calendar') &&
                !key.toLowerCase().includes('google') &&
                !key.toLowerCase().includes('microsoft')
            )
          )
        case 'team':
          // Team step completed when there's at least one other member (accepted or pending)
          if (invitationsLoading || membersLoading) {
            return false
          } else {
            // subtract current user from workspaceMembers
            const totalOthers = (pendingInvitations?.length || 0) + (workspaceMembers?.length || 0) - 1
            return totalOthers > 0
          }
        default:
          return false
      }
    },
    [
      activeWorkspace?.onboarding_completed_steps,
      products,
      meetings,
      user?.integrations,
      invitationsLoading,
      pendingInvitations,
      membersLoading,
      workspaceMembers,
    ]
  )

  // Check if all onboarding steps are completed (excluding skipped ones)
  const areAllOnboardingStepsCompleted = useCallback(() => {
    // Product step must be completed first
    if (!checkStepCompletion('product')) return false

    const optionalSteps = ['recordings', 'integrations', 'team']
    const skipped = activeWorkspace?.onboarding_skipped_steps || []

    return optionalSteps.every((step) => checkStepCompletion(step) || skipped.includes(step))
  }, [checkStepCompletion, activeWorkspace])

  // Helper function to determine if onboarding should be shown based on the workspace flag
  const shouldShowOnboarding = () => {
    // Avoid showing anything until the essential data has finished loading
    if (projectsLoading || eventsLoading || isLoading || invitationsLoading || membersLoading) return false

    // If the workspace document does not yet have the flag (legacy workspaces), assume onboarding was already completed
    if (activeWorkspace?.show_onboarding === undefined) return false

    return activeWorkspace.show_onboarding
  }

  // Check if current page is a roadmap page, using the location pathname
  const isRoadmapPage = location.pathname.startsWith('/dashboard/roadmap')
  const isHomeNew = selectedMenu === 'home-new'
  // Determine if onboarding status has been evaluated (all relevant loading flags are false)
  const onboardingEvaluationReady = !(
    projectsLoading ||
    eventsLoading ||
    isLoading ||
    invitationsLoading ||
    membersLoading
  )

  const handleSubscribeToPlan = async (workspaceId, planId) => {
    try {
      const response = await customFetch('/createCheckoutUrl', 'POST', { workspaceId, planId })
      if (!response.hostedPageUrl) throw new Error('No plan URL found')
      window.location.href = response.hostedPageUrl
    } catch (error) {
      console.error('Error initiating payment', error)
    }
  }

  // Fetch invitations when user/workspace changes
  useEffect(() => {
    if (user?.active_workspace) {
      fetchInvitations()
    }
  }, [user?.active_workspace, fetchInvitations])

  // Fetch members when user/workspace changes
  useEffect(() => {
    if (user?.active_workspace) {
      fetchMembers()
    }
  }, [user?.active_workspace, fetchMembers])

  // When all steps are done (or skipped), flip the show_onboarding flag and then navigate
  useEffect(() => {
    // Ensure we have the necessary data loaded
    if (activeWorkspace?.show_onboarding === false) return // already disabled
    if (projectsLoading || eventsLoading || isLoading || invitationsLoading || membersLoading) return

    if (areAllOnboardingStepsCompleted()) {
      ;(async () => {
        try {
          await customFetch('/updateWorkspace', 'PUT', {
            workspaceId: user.active_workspace,
            updates: { show_onboarding: false },
          })
          setActiveWorkspace({ ...activeWorkspace, show_onboarding: false })
          // After flipping the flag, navigate the user to the new home
          if (selectedMenu === 'home') {
            navigate('/dashboard/home-new', { replace: true })
          }
        } catch (err) {
          console.error('Failed to update show_onboarding flag', err)
        }
      })()
    }
  }, [
    activeWorkspace?.show_onboarding,
    projectsLoading,
    eventsLoading,
    isLoading,
    invitationsLoading,
    membersLoading,
    areAllOnboardingStepsCompleted,
    customFetch,
    user.active_workspace,
    setActiveWorkspace,
    selectedMenu,
    navigate,
  ])

  // Navigate to home-new when onboarding is completed
  useEffect(() => {
    // Only check for auto-navigation when user is currently viewing onboarding
    if (
      selectedMenu === 'home' &&
      !shouldShowOnboarding() &&
      !projectsLoading &&
      !eventsLoading &&
      !isLoading &&
      !invitationsLoading &&
      !membersLoading
    ) {
      // User completed onboarding and is still on the home tab, redirect to home-new
      navigate('/dashboard/home-new', { replace: true })
    }
  }, [
    selectedMenu,
    shouldShowOnboarding,
    projectsLoading,
    eventsLoading,
    isLoading,
    invitationsLoading,
    membersLoading,
    navigate,
  ])

  useEffect(() => {
    if (isLoading || eventsLoading) return
    ;(async function () {
      await new Promise((resolve) => setTimeout(resolve, 1000))
      setShowLoader(false)
    })()
  }, [filteredProjects, upcomingMeetings, eventsLoading, isLoading])

  useEffect(() => {
    if (typeof user !== 'object' || Object.keys(user).length === 0) return
    if (!activeWorkspace) return
    const paymentStatus = searchParams.get('payment_status')
    if (paymentStatus === 'success') return
    const planId = searchParams.get('plan_id')
    if (!planId) return
    const workspacesAmount = Object.keys(user.workspaces).length
    if (workspacesAmount !== 1) return
    if (activeWorkspace.plan_id) {
      navigate(`/workspace/select?plan_id=${planId}`)
    } else {
      handleSubscribeToPlan(Object.keys(user.workspaces)[0], planId)
    }
  }, [activeWorkspace])

  useEffect(() => {
    if (typeof user !== 'object' || Object.keys(user).length === 0) return
    // check if this is a redirect from the payment gateway
    const paymentStatus = searchParams.get('payment_status')
    const planId = searchParams.get('plan_id')
    if (paymentStatus === 'success') {
      toast.success('Payment successful, might take a few minutes to reflect in your account')
      setTimeout(refreshActiveWorkspace, 30000)
      return
    }
    if (planId) {
      const workspacesAmount = Object.keys(user.workspaces).length
      if (workspacesAmount === 0) {
        navigate(`/workspace/create?plan_id=${planId}`)
      } else if (workspacesAmount > 1) {
        navigate(`/workspace/select?plan_id=${planId}`)
      }
    }
  }, [user.workspaces])

  useEffect(() => {
    const paymentStatus = searchParams.get('payment_status')
    const planId = searchParams.get('plan_id')
    if (paymentStatus === 'success' || !planId) {
      setIsLoading(false)
    }
  }, [searchParams])

  return (
    <div className="flex h-screen bg-white dark:bg-gray-900">
      <div
        className={`h-full flex-shrink-0 border-r-2 border-grey-200 dark:border-gray-700 overflow-y-auto hideScrollbar ${isCollapsed ? 'w-[64px]' : 'w-[200px]'}`}
      >
        <Sidebar selectedMenu={selectedMenu} setSelectedMenu={setSelectedMenu} isOnboarding={shouldShowOnboarding()} />
      </div>
      <div className="flex flex-1 flex-col overflow-hidden">
        {!isRoadmapPage && (
          <div className="h-[70px] flex-shrink-0 border-b-2 border-grey-200 dark:border-gray-700 z-10">
            <Topbar />
          </div>
        )}

        <div
          className={clsx(
            'flex-1 hideScrollbar z-0 h-[calc(100vh-var(--topbar-height))]',
            !isRoadmapPage &&
              `${
                selectedMenu === 'chats' ? '' : !isHomeNew ? 'xl:pl-10 sm:pl-4 pb-10 overflow-y-auto' : ''
              } border-b-2 border-grey-200 dark:border-gray-700 bg-grey-100 dark:bg-gray-800`,
            (!!allProjects.length || !!upcomingMeetings.length) &&
              selectedMenu === 'home' &&
              'grid grid-cols-1 md:grid-cols-6 gap-4'
          )}
          style={{
            paddingLeft:
              selectedMenu === 'chats'
                ? '0px'
                : shouldShowOnboarding() && (selectedMenu === 'home' || selectedMenu === 'home-new')
                  ? '0px'
                  : !allProjects.length && !upcomingMeetings.length && selectedMenu === 'home'
                    ? '0px'
                    : undefined,
          }}
        >
          <div className={clsx('h-full', !isRoadmapPage && 'md:col-span-6 xl:col-span-4')}>
            {selectedMenu === 'home' && (
              <>
                {!onboardingEvaluationReady ? null : shouldShowOnboarding() ? (
                  <OnboardingWelcomePage />
                ) : !!allProjects.length || !!upcomingMeetings.length ? (
                  <>
                    <Events limit={limit} events={upcomingMeetings} eventsLoading={eventsLoading} />
                    <RecentMeetings setSelectedMenu={setSelectedMenu} />
                    <Projects limit={limit} />
                  </>
                ) : (
                  <WelcomeDashboard />
                )}
              </>
            )}
            {selectedMenu === 'home-new' && (
              <>
                {!onboardingEvaluationReady ? null : shouldShowOnboarding() ? <OnboardingWelcomePage /> : <HomeNew />}
              </>
            )}
            {selectedMenu === 'projects' && <Projects />}
            {selectedMenu === 'meetings' && <Meetings />}
            {selectedMenu === 'competitors' && <Competitors />}
            {selectedMenu === 'recruiting' && <Recruiting />}
            {selectedMenu === 'integrations' && <Integrations />}
            {selectedMenu === 'chats' && <Chats />}
            {selectedMenu === 'team' && <Team />}
            {selectedMenu === 'team-settings' && <Settings />}
            {selectedMenu === 'products' && <Products />}
            {selectedMenu === 'roadmap' && <Roadmap />}
          </div>
          {selectedMenu === 'home' &&
            !shouldShowOnboarding() &&
            (!!allProjects.length || !!upcomingMeetings.length) && (
              <div className="md:col-span-6 xl:col-span-2">
                <DynamicItems
                  vertical={!!allProjects.length || !!upcomingMeetings.length}
                  padding={null}
                ></DynamicItems>
              </div>
            )}
        </div>
      </div>

      {/* Mobile Warning Dialog */}
      {isMobile && (
        <AlertDialog open={showMobileWarning} onOpenChange={setShowMobileWarning}>
          <AlertDialogContent className="w-[calc(100%-2rem)] max-w-lg rounded-2xl left-[50%] translate-x-[-50%]">
            <AlertDialogHeader className="items-center">
              <AlertDialogTitle className="flex items-center justify-center text-lg">
                Evermuse is Best on Desktop
              </AlertDialogTitle>
              <AlertDialogDescription className="text-base leading-relaxed pt-2 text-center">
                <span className="font-bold">Evermuse is optimized to work best on desktop.</span> We have not yet
                prioritized testing or optimizing for mobile.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <div className="flex justify-center py-6">
              <MonitorSmartphone className="h-20 w-20 text-gray-400" strokeWidth={1.5} />
            </div>
            <AlertDialogFooter className="flex-col sm:flex-row gap-2">
              <AlertDialogAction onClick={handleSignOut} className="w-full sm:w-auto bg-[#FF4A8E] hover:bg-[#E43D7A]">
                Sign out
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </div>
  )
}
